import React, { useEffect } from "react";

import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { FlagIcon } from "@heroicons/react/24/solid";
import ReactTooltip from "react-tooltip";
import { useGetBlueprintAnnotatedList } from "../../../api/BlueprintsApi";
import LoadingSpinner from "../../../shared-components/LoadingSpinner";
import ErrorMessageContainer from "../../../shared-components/ErrorMessageContainer";

import {
  BehaviorInfoMinimal,
  READABLE_TURN_AROUND,
  TURN_AROUND_CALCULATIONS,
} from "../../../api/Blueprints";

import { behaviorRelabelMapping, useFamaFlags } from "../../../api/FamaFlags";
import { blueprint_tooltip, sortNamedListAlpha } from "../../../utils";

type Props = {
  blueprintUUID: string;
  isVisible: boolean;
};

const CheckTypeDetail = ({ blueprintUUID, isVisible }: Props) => {
  const {
    mutate: getBlueprintsInfo,
    data: blueprintInfo,
    isLoading: isLoadingBlueprintsInfo,
    error: errorBlueprintsInfo,
  } = useGetBlueprintAnnotatedList();

  useEffect(() => {
    if (!blueprintInfo && isVisible) {
      getBlueprintsInfo([blueprintUUID]);
    }
  }, [getBlueprintsInfo, blueprintUUID, isVisible, blueprintInfo]);

  if (isLoadingBlueprintsInfo) return <LoadingSpinner />;

  if (!isVisible || !blueprintInfo) return <></>;
  const blueprint = blueprintInfo?.[0];
  if (!blueprint || errorBlueprintsInfo)
    return (
      <ErrorMessageContainer message="Error loading Blueprint information" />
    );

  const turn_around = blueprint.turn_around;
  let turn_around_display: string = "Unknown";
  if (turn_around === TURN_AROUND_CALCULATIONS.Days) {
    turn_around_display = READABLE_TURN_AROUND.Days;
  } else if (turn_around === TURN_AROUND_CALCULATIONS.SingleBusinessDay) {
    turn_around_display = READABLE_TURN_AROUND.SingleBusinessDay;
  } else if (turn_around === TURN_AROUND_CALCULATIONS.ThreeSixty) {
    turn_around_display = READABLE_TURN_AROUND.ThreeSixty;
  }

  return (
    <div className="mt-4 text-sm border-t border-gray-200">
      <CheckTypeFlags behaviors={blueprint.behaviors} showActiveOnly={true} />
      <div>
        <div className="flex rounded-md gap-2 p-2 font-medium">
          Report SLA Calculations
          <span data-tip={blueprint_tooltip}>
            <InformationCircleIcon height={24} width={24} color="gray" />
          </span>
          <ReactTooltip
            className="hover:visible:opacity-1 w-96"
            place="right"
            delayHide={1000}
            effect="solid"
            clickable={true}
            html={true}
          />
        </div>
        {blueprint.turn_around === TURN_AROUND_CALCULATIONS.Hour ? (
          <div className="flex p-2 gap-2 my-2">
            <span>Check Type SLA (in Hours): {blueprint.sla_hours}</span>
          </div>
        ) : (
          <div className="flex p-2 gap-2 my-2">{turn_around_display}</div>
        )}
        <div className="rounded-md  p-2 font-medium">Lookback:</div>
        <span className="flex p-2 gap-2 my-2">
          {"Lookback period for blueprint (months): "}
          <b>{blueprint.lookback}</b>
        </span>
      </div>
    </div>
  );
};

type PropsFlags = {
  behaviors?: BehaviorInfoMinimal[];
  showActiveOnly?: boolean;
};

export function CheckTypeFlags({ behaviors, showActiveOnly }: PropsFlags) {
  const { behaviorMappingActions } = useFamaFlags();
  function buildBehaviorsNameList() {
    if (!behaviors) return [];

    const list = sortNamedListAlpha(
      Array.from(
        new Set(
          behaviors
            ?.filter((pivot) => (showActiveOnly ? pivot.is_active : true))
            .map((pivot) => pivot.name)
        )
      )
    );
    return list;
  }

  if (!behaviors) return <></>;
  const sortedBehaviors = buildBehaviorsNameList();
  const relabeledBehaviors = behaviorRelabelMapping(
    sortedBehaviors,
    behaviorMappingActions
  );
  return (
    <div className="mt-2 text-sm">
      <div className="rounded-md p-2 font-medium">Fama behavior flag:</div>
      {sortedBehaviors.length > 0 ? (
        <div className="flex gap-3 p-2 flex-wrap my-2">
          {relabeledBehaviors.map((text, idx) => (
            <div className="flex gap-2" key={idx}>
              <FlagIcon className="w-4 text-green-600" />
              <span className="capitalize">{text.toLowerCase()}</span>
            </div>
          ))}
        </div>
      ) : (
        <div className="py-2 pl-2">No Assigned Behaviors</div>
      )}
    </div>
  );
}

export default CheckTypeDetail;
